import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Pencil, Trash2, Shield, Gavel, MessageSquareWarning, Download, Search, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { EmptyState, SectionHeader, schoolYear } from "@/components/PageHeader";
import MemberSummaryDialog from "@/components/members/MemberSummaryDialog";
import { MemberLetterAwards, MissedYearsPicker } from "@/components/lettering/MemberLetterAwards";
import { subsequentPaths } from "@/lib/letterPolicy";
import { useFeatureFlags } from "@/lib/featureFlags";
import { fmtPts } from "@/components/thespian/ThespianRankCard";
import { TAP_ROW, TAP_BG, TAP_HOVER_BG, TAP_ARROW_CELL, TapArrow, TapInfo } from "@/components/TapRow";
import PendingApplications from "./AssignmentRequests";

const usesActiveYears = (p) => !!(p?.first?.active_years?.enabled || subsequentPaths(p)?.active_years?.enabled);

export default function CoachMembers() {
  const { viewOnly } = useAuth();
  const { thespian: thespianOn, plan } = useFeatureFlags();
  const columns = ["Name", "Email", "Graduation Year", "Merits", "Demerits", "Net", "Lettering", ...(thespianOn ? ["Thespian"] : []), "Standing", "Active", "Actions", ""];

  const exportCsv = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusId, setFocusId] = useState(null);
  const [items, setItems] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", is_active: true, graduation_year: "", good_standing: true, first_letter: false, subgroup_ids: [] });
  const [del, setDel] = useState(null);
  const [sort, setSort] = useState("name");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);

  const openDetail = async (m) => {
    try { const { data } = await api.get(`/coach/team-members/${m.id}`); setDetail(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = useCallback(async () => {
    const [m, s] = await Promise.all([api.get("/coach/team-members"), api.get("/coach/subgroups")]);
    setItems(m.data.items);
    setSubgroups(s.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep-link from a notification: /coach/members?member=<id>&focus=<case#> auto-opens
  // that member's drill-in and briefly highlights the exact case/request.
  useEffect(() => {
    const mid = searchParams.get("member");
    if (!mid) return;
    const focus = searchParams.get("focus");
    openDetail({ id: mid });
    if (focus) { setFocusId(focus); setTimeout(() => setFocusId(null), 6000); }
    searchParams.delete("member");
    searchParams.delete("focus");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const subMap = Object.fromEntries(subgroups.map((s) => [s.id, s.name]));

  const byName = (a, b) => `${a.last_name}, ${a.first_name}`.localeCompare(`${b.last_name}, ${b.first_name}`);
  const sorted = [...items].sort((a, b) => {
    if (sort === "grad") return (b.graduation_year || 0) - (a.graduation_year || 0) || byName(a, b);
    return (a.is_active === false) - (b.is_active === false) || byName(a, b);
  });

  // Live name search: any part of the first or last name.
  const q = query.trim().toLowerCase();
  const visible = q ? sorted.filter((m) => `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase().includes(q)) : sorted;

  // Year picker shows when the coach is granting the letter now, or it was already coach-granted.
  const curSY = editing?.letter_status?.active_year || (new Date().getMonth() >= 5 ? new Date().getFullYear() : new Date().getFullYear() - 1);
  const letterYearOptions = Array.from({ length: 8 }, (_, i) => curSY - i);
  const pickLetterYear = !!editing && (!editing.letter_status?.first_letter_earned || editing.first_letter_override === true);

  const openEdit = (m) => {
    setEditing(m);
    setForm({ first_name: m.first_name, last_name: m.last_name, email: "", is_active: m.is_active !== false, graduation_year: m.graduation_year || "", good_standing: m.good_standing !== false, first_letter: !!m.letter_status?.first_letter_earned, subgroup_ids: m.subgroup_ids || [],
      first_letter_year: String(m.first_letter_override_year || m.letter_status?.first_letter_year || m.letter_status?.active_year || ""),
      first_letter_reason: m.first_letter_override_reason || "", missed_years: m.letter_missed_years || [] });
  };
  const doEdit = async () => {
    const payload = { ...form };
    delete payload.good_standing;
    delete payload.first_letter;
    delete payload.first_letter_year;
    delete payload.first_letter_reason;
    delete payload.missed_years;
    delete payload.subgroup_ids;
    // Email is masked on coach pages; only update it if the coach typed a new one.
    if (!payload.email || !payload.email.trim()) delete payload.email;
    // Only send override when the coach actually changed the toggle,
    // so unrelated edits don't permanently pin the auto (computed) calculation.
    const original = editing?.good_standing !== false;
    if (form.good_standing !== original) payload.good_standing_override = form.good_standing;
    const origFirst = !!editing?.letter_status?.first_letter_earned;
    if (form.first_letter !== origFirst) payload.first_letter_override = form.first_letter;
    // Coach-granted First Letter: the school year it counts for (Additional Awards start the year after).
    if (form.first_letter && pickLetterYear && form.first_letter_year
        && (form.first_letter !== origFirst || Number(form.first_letter_year) !== editing?.first_letter_override_year)) {
      payload.first_letter_override_year = Number(form.first_letter_year);
    }
    const reason = (form.first_letter_reason || "").trim();
    if (form.first_letter && !origFirst && !reason) { toast.error("Add a reason note for the First Letter award"); return; }
    if (form.first_letter && pickLetterYear && reason && reason !== (editing?.first_letter_override_reason || "")) payload.first_letter_reason = reason;
    const missed = [...form.missed_years].sort();
    if (JSON.stringify(missed) !== JSON.stringify([...(editing?.letter_missed_years || [])].sort())) payload.letter_missed_years = missed;
    if (payload.graduation_year) payload.graduation_year = Number(payload.graduation_year); else delete payload.graduation_year;
    try { await api.put(`/coach/team-members/${editing.id}`, payload); await api.put(`/coach/team-members/${editing.id}/subgroups`, { subgroup_ids: form.subgroup_ids }); toast.success("Member updated"); setEditing(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/coach/team-members/${del.id}`); toast.success("Member deactivated"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  return (
    <div>
      <PendingApplications onChanged={load} />
      <SectionHeader subtitle="Edit and manage your approved team members" testId="roster-section-header"
        actions={viewOnly ? <><Button variant="outline" className="rounded-full" onClick={() => exportCsv("/coach/team-members/export.csv", "team-roster.csv")} data-testid="oversight-roster-csv-btn"><Download className="mr-2 h-4 w-4" /> Roster CSV</Button><Button variant="outline" className="rounded-full" onClick={() => exportCsv("/coach/merits/export.csv", "merit-history.csv")} data-testid="oversight-merits-csv-btn"><Download className="mr-2 h-4 w-4" /> Merit History CSV</Button></> : null} />
      {items.length === 0 ? <EmptyState title="No approved members" description="Approve signup requests to see members here." testId="coach-members-empty" /> : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name…" aria-label="Search members by name"
                className="rounded-full pl-9 pr-9" data-testid="coach-members-search" />
              {query && (
                <button type="button" onClick={() => setQuery("")} aria-label="Clear search" data-testid="coach-members-search-clear"
                  className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Sort by</Label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-56" data-testid="coach-members-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="name" data-testid="sort-name">Name (A–Z)</SelectItem>
                  <SelectItem value="grad" data-testid="sort-grad">Graduation Year (High → Low)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {q && <span className="text-xs text-slate-500" data-testid="coach-members-search-count">{visible.length} of {items.length} members</span>}
          </div>
          <Card className="border-slate-200 shadow-sm overflow-x-auto">
            <Table data-testid="coach-members-table" className="[&_td]:text-center [&_th]:text-center">
              <TableHeader><TableRow className="bg-slate-50">{columns.map((h) => <TableHead key={h || "open"} className={`text-xs font-bold uppercase tracking-wide text-slate-500 ${h === "Name" ? "sticky left-0 z-10 bg-slate-50 shadow-[1px_0_0_0_#e2e8f0]" : ""} ${h === "" ? "sticky right-0 z-10 bg-slate-50" : ""}`}>{h === "Name" ? <span className="inline-flex items-center gap-2"><TapInfo tip="Tap any member's row to open their summary." testId="roster-tap-info" />Name</span> : h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {visible.length === 0 ? (
                  <TableRow><TableCell colSpan={columns.length} className="py-8 text-center text-sm text-slate-400" data-testid="coach-members-empty">{q ? `No members match “${query.trim()}”.` : "No team members yet."}</TableCell></TableRow>
                ) : visible.map((m) => (
                  <TableRow key={m.id} data-testid={`coach-member-row-${m.id}`} onClick={() => openDetail(m)} className={`${TAP_ROW} ${m.is_active === false ? "opacity-50" : ""}`}>
                    <TableCell className={`sticky left-0 z-10 whitespace-nowrap font-semibold text-slate-800 shadow-[1px_0_0_0_#e2e8f0] ${TAP_BG} ${TAP_HOVER_BG}`} data-testid={`coach-member-name-${m.id}`}>
                      <div className="flex items-center justify-center gap-2">
                        <span>{m.last_name}, {m.first_name}</span>
                        {m.pending_conference_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-rose-600" title={`${m.pending_conference_count} requested conference(s) — awaiting a date`} data-testid={`coach-member-conference-${m.id}`}>
                            <Gavel className="h-4 w-4" />
                            {m.pending_conference_count > 1 && <span className="text-xs font-bold">{m.pending_conference_count}</span>}
                          </span>
                        )}
                        {m.pending_lettering > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-violet-600" title={`${m.pending_lettering} lettering award request(s) to review`} data-testid={`coach-member-review-lettering-${m.id}`}>
                            <Shield className="h-4 w-4" />
                            {m.pending_lettering > 1 && <span className="text-xs font-bold">{m.pending_lettering}</span>}
                          </span>
                        )}
                        {m.pending_merit_requests > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-amber-600" title={`${m.pending_merit_requests} merit request(s) to review`} data-testid={`coach-member-review-merit-${m.id}`}>
                            <MessageSquareWarning className="h-4 w-4" />
                            {m.pending_merit_requests > 1 && <span className="text-xs font-bold">{m.pending_merit_requests}</span>}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{m.email}</TableCell>
                    <TableCell className="tabular-nums">{m.graduation_year || "—"}</TableCell>
                    <TableCell className="tabular-nums font-semibold text-emerald-600" data-testid={`coach-member-merits-${m.id}`}>{m.total_merits ?? 0}</TableCell>
                    <TableCell className="tabular-nums font-semibold text-red-600" data-testid={`coach-member-demerits-${m.id}`}>{m.total_demerits ?? 0}</TableCell>
                    <TableCell className={`tabular-nums font-bold ${(m.net_merit_score ?? 0) < 0 ? "text-red-600" : "text-slate-800"}`} data-testid={`coach-member-net-${m.id}`}>{m.net_merit_score ?? 0}</TableCell>
                    <TableCell className="font-bold tabular-nums text-violet-700" data-testid={`coach-member-lettering-${m.id}`}>{m.letter_status?.total_approved_points || 0}</TableCell>
                    {thespianOn && (
                      <TableCell className="font-bold tabular-nums text-amber-700" data-testid={`coach-member-thespian-${m.id}`}>{fmtPts(m.thespian_points || 0)}</TableCell>
                    )}
                    <TableCell>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.good_standing !== false ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`} data-testid={`coach-member-standing-${m.id}`}>
                        {m.good_standing !== false ? "Good" : "Not good"}
                      </span>
                    </TableCell>
                    <TableCell>{m.is_active === false ? "No" : "Yes"}</TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openEdit(m); }} data-testid={`coach-member-edit-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={(e) => { e.stopPropagation(); setDel(m); }} data-testid={`coach-member-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                    <TableCell className={TAP_ARROW_CELL}><TapArrow testId={`coach-member-open-${m.id}`} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="coach-member-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="coach-member-firstname" /></div>
              <div className="space-y-2"><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="coach-member-lastname" /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder={editing?.email ? `${editing.email} (leave blank to keep)` : "Enter email"} data-testid="coach-member-email" /><p className="text-xs text-slate-400">For privacy, emails are masked. Type a new email only if you want to change it.</p></div>
            <div className="space-y-2"><Label>Graduation Year</Label><Input type="number" value={form.graduation_year} onChange={(e) => setForm((f) => ({ ...f, graduation_year: e.target.value }))} placeholder="e.g. 2028" data-testid="coach-member-gradyear" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="coach-member-active" /><Label>Active (can log in)</Label></div>
            <div className="flex items-center gap-3"><Switch checked={form.good_standing} onCheckedChange={(v) => setForm((f) => ({ ...f, good_standing: v }))} data-testid="coach-member-good-standing" /><Label>Good standing (lettering eligible)</Label></div>
            <p className="text-xs text-slate-400">Turning this off marks the member as not in good standing and greys their lettering award badge.</p>
            <div className="flex items-center gap-3"><Switch checked={form.first_letter} onCheckedChange={(v) => setForm((f) => ({ ...f, first_letter: v }))} data-testid="coach-member-first-letter" /><Label>First letter award earned</Label></div>
            <p className="text-xs text-slate-400">Manually grant or revoke the first letter award (coach discretion). Granting needs a reason note.</p>
            {form.first_letter && pickLetterYear && (
              <div className="space-y-2" data-testid="coach-member-first-letter-year-field">
                <Label>School year the First Letter was earned</Label>
                <Select value={form.first_letter_year} onValueChange={(v) => setForm((f) => ({ ...f, first_letter_year: v }))}>
                  <SelectTrigger className="w-48" data-testid="coach-member-first-letter-year"><SelectValue placeholder="Pick a year" /></SelectTrigger>
                  <SelectContent>
                    {letterYearOptions.map((y) => <SelectItem key={y} value={String(y)} data-testid={`first-letter-year-${y}`}>{schoolYear(y)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">Subsequent Letters can be earned starting the school year after this one.</p>
                <Label>Reason note{editing?.first_letter_override === true ? "" : " (required)"}</Label>
                <Textarea rows={2} value={form.first_letter_reason} onChange={(e) => setForm((f) => ({ ...f, first_letter_reason: e.target.value }))} placeholder="Why are you awarding this letter?" data-testid="coach-member-first-letter-reason" />
              </div>
            )}
            {editing && <MemberLetterAwards key={editing.id} memberId={editing.id} status={editing.letter_status} onChanged={load} />}
            {editing && usesActiveYears(editing.letter_status?.policy) && (
              <MissedYearsPicker joinYear={editing.letter_status?.join_year} curYear={editing.letter_status?.active_year}
                value={form.missed_years} onChange={(v) => setForm((f) => ({ ...f, missed_years: v }))} />
            )}
            {subgroups.length > 0 && (
              <div className="space-y-2">
                <Label>Groups</Label>
                <div className="flex flex-wrap gap-2" data-testid="coach-member-groups-select">
                  {subgroups.map((s) => {
                    const on = form.subgroup_ids.includes(s.id);
                    return (
                      <button type="button" key={s.id} data-testid={`coach-member-group-${s.id}`}
                        onClick={() => setForm((f) => ({ ...f, subgroup_ids: on ? f.subgroup_ids.filter((x) => x !== s.id) : [...f.subgroup_ids, s.id] }))}
                        className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400">Assign this member to one or more groups. Manage the group list on the Groups page.</p>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={doEdit} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="coach-member-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!del} onOpenChange={(o) => !o && setDel(null)}
        title="Deactivate team member?"
        description={del ? `${del.first_name} ${del.last_name} (${del.email}) will be deactivated and can no longer log in. Their merit and lettering records are preserved, and you can reactivate them from the edit dialog.` : ""}
        confirmText="Deactivate" destructive onConfirm={doDelete} testId="coach-member-delete-dialog"
      />

      <MemberSummaryDialog detail={detail} onClose={() => setDetail(null)} focusId={focusId} showThespian={plan === "theatre"}
        groups={detail ? (items.find((x) => x.id === detail.member.id)?.subgroup_ids || []).filter((id) => subMap[id]).map((id) => subMap[id]) : []} />
    </div>
  );
}
